// User-facing copy for Creative Studio batch uploads.
//
// The previous message was built by unconditional interpolation:
//
//   `Upload failed for ${n === 1 ? "the file" : "all files"}: ${names.join(", ")}.`
//
// which had two defects. It reported "all files" whenever the failure list
// was non-empty — even when five of six had actually uploaded — and it
// concatenated fragments without checking whether they were empty, which is
// how an empty tail produced dangling copy such as "and .".
//
// Everything here is a pure function over counts and names so the exact
// strings are unit-testable, and every join goes through `joinList`, which
// cannot emit a dangling conjunction because it filters empties FIRST.

/**
 * Oxford-comma list that is safe on empty input.
 *
 * The old bug was joining a list that could contain empty strings, leaving a
 * trailing "and ." Filtering before deciding the shape makes that shape
 * unreachable rather than merely unlikely.
 */
export function joinList(values) {
  const clean = (Array.isArray(values) ? values : [])
    .map((value) => String(value == null ? "" : value).trim())
    .filter((value) => value.length > 0);

  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
}

/** "1 creative" / "4 creatives" — never "1 creatives". */
export function pluralize(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural || `${singular}s`}`;
}

/**
 * Group failures by their reason so a six-file batch reports
 * "Two files exceeded the current upload limit" rather than six near-
 * identical sentences.
 */
function summarizeReasons(failures) {
  const groups = new Map();
  for (const failure of failures) {
    const key = failure?.code || "unknown";
    if (!groups.has(key)) groups.set(key, { code: key, message: failure?.message || "", names: [] });
    groups.get(key).names.push(failure?.name || "a file");
  }
  return [...groups.values()];
}

// Each phrase is a COMPLETE clause carrying its own subject-verb agreement.
// Building agreement here rather than in the caller is what keeps a mixed
// batch ("2 files exceeded... and 1 file is...") grammatical — the earlier
// shape appended one plural tail to every clause and produced "One file are".
const REASON_PHRASES = Object.freeze({
  creative_upload_file_too_large: (n) =>
    `${n} ${n === 1 ? "file" : "files"} exceeded the current upload limit`,
  creative_upload_unsupported_extension: (n) =>
    `${n} ${n === 1 ? "file is" : "files are"} not a supported creative type`,
  creative_upload_empty_file: (n) => `${n} ${n === 1 ? "file was" : "files were"} empty`,
  creative_upload_session_expired: (n) =>
    `${n} ${n === 1 ? "upload" : "uploads"} expired before finishing`,
  creative_upload_transport_failed: (n) =>
    `${n} ${n === 1 ? "upload lost its" : "uploads lost their"} connection to storage`,
  // Interruptions are NOT failures of the file — they are failures of the
  // connection, and the bytes already in storage survive them.
  creative_upload_interrupted_resumable: (n) =>
    `${n} ${n === 1 ? "upload was" : "uploads were"} interrupted and can resume`,
  creative_upload_interrupted_empty: (n) =>
    `${n} ${n === 1 ? "upload was" : "uploads were"} interrupted before anything was saved`,
  creative_upload_interrupted_unknown: (n) =>
    `${n} ${n === 1 ? "upload was" : "uploads were"} interrupted`,
  creative_upload_finalize_failed: (n) =>
    `${n} ${n === 1 ? "upload finished but was" : "uploads finished but were"} not completed`,
});

function phraseFor(group) {
  const build = REASON_PHRASES[group.code];
  const count = group.names.length;
  if (build) return build(count);
  // Unknown reasons fall back to the server's own sentence when it gave one,
  // so a specific backend message is never replaced by something vaguer.
  if (group.message) return group.message.replace(/\.$/, "");
  return `${count} ${count === 1 ? "file" : "files"} could not be uploaded`;
}

/**
 * The batch outcome banner.
 *
 * Honest by construction: it states how many of how many succeeded and never
 * claims total failure while successes exist.
 */
export function buildUploadSummary({ succeeded = [], failed = [], total = 0 } = {}) {
  const successCount = succeeded.length;
  const failureCount = failed.length;
  const totalCount = total || successCount + failureCount;

  if (totalCount === 0) return { tone: "none", message: "" };

  if (failureCount === 0) {
    return {
      tone: "success",
      message:
        successCount === 1
          ? "Creative uploaded."
          : `${pluralize(successCount, "creative")} uploaded.`,
    };
  }

  const reasonSentence = joinList(summarizeReasons(failed).map(phraseFor));

  if (successCount === 0) {
    // Total failure — the only case where "none uploaded" is truthful.
    const detail = reasonSentence ? `${reasonSentence}.` : "";
    return {
      tone: "error",
      message: [
        totalCount === 1 ? "The creative was not uploaded." : `None of the ${totalCount} creatives were uploaded.`,
        detail,
      ]
        .filter(Boolean)
        .join(" "),
    };
  }

  // Partial success. The successes are real and are stated first.
  // A single reason takes the "and were not uploaded" tail (the clearest
  // reading); several reasons are already complete clauses, so appending one
  // shared tail would mis-agree with all but the last of them.
  const groupCount = summarizeReasons(failed).length;
  const detail = reasonSentence
    ? groupCount === 1
      ? `${reasonSentence} and ${failureCount === 1 ? "was" : "were"} not uploaded.`
      : `${reasonSentence}.`
    : "";
  return {
    tone: "partial",
    message: [`${successCount} of ${totalCount} creatives uploaded.`, detail].filter(Boolean).join(" "),
  };
}

/**
 * Per-file detail lines. The user can see exactly which file failed and why,
 * instead of a single comma-joined blob of six filenames.
 */
export function buildFailureDetails(failed = []) {
  return failed
    .filter(Boolean)
    .map((failure) => ({
      name: failure.name || "a file",
      // `title` distinguishes an interruption (recoverable) from a refusal
      // (not). Without it every outcome read as the same flat failure.
      title: failure.title || null,
      message: failure.message || "This file could not be uploaded.",
      code: failure.code || null,
      // What the user can actually do about it, decided by what STORAGE
      // confirmed — not guessed from the fact that an error occurred.
      action: failure.action || "retry",
      actionLabel: actionLabelFor(failure),
      confirmedBytes:
        typeof failure.confirmedBytes === "number" ? failure.confirmedBytes : null,
      total: typeof failure.total === "number" ? failure.total : null,
      // A support-safe handle. Meaningless without access, but it lets an
      // operator find the exact server-side logs for this one upload.
      reference: failure.uploadSessionId || null,
    }));
}

/**
 * The button an interrupted upload should offer.
 *
 * "Resume" is only ever shown when storage has confirmed durable bytes (or
 * has told us the object is already whole). Offering "Resume" for an upload
 * with nothing behind it would be the same species of lie as the message
 * that claimed nothing was saved when everything had been.
 */
export function actionLabelFor(failure = {}) {
  if (failure.action === "resume") return "Resume upload";
  if (failure.code === "creative_upload_session_expired") return "Retry upload";
  return "Retry upload";
}

/**
 * Human progress for an interrupted upload — "612.5 MB of 716.6 MB already
 * saved" — so "resume" is a visible fact rather than a promise.
 */
export function describeConfirmedProgress(failure, formatBytes) {
  if (!failure || typeof failure.confirmedBytes !== "number" || !failure.total) return "";
  if (failure.confirmedBytes <= 0) return "";
  return `${formatBytes(failure.confirmedBytes)} of ${formatBytes(failure.total)} already saved.`;
}

/** Copy for the retry affordance — only failed files are ever retried. */
export function buildRetryLabel(failedCount) {
  if (!failedCount) return "";
  return failedCount === 1 ? "Retry the failed file" : `Retry ${failedCount} failed files`;
}

export default buildUploadSummary;
